import { Types } from "assets/theme/base/colors";
import { HMO, HMOType } from "constants/hmo";

/**
 * HMO Color Schemes
 * Defines color palettes for each HMO portal
 * 
 * React 19 Best Practice: Pre-computed module-level constants
 * Since HMO is detected once and never changes, we pre-compute all possible values
 * This eliminates runtime computation and aligns with React 19's immutable patterns
 */

// Pre-computed HMO color constants (React 19 best practice - compile-time constants)
const HMO_COLORS: Record<HMO, Partial<Types>> = {
  [HMO.LEUMIT]: {
    primary: {
      main: '#1C6BA4',
      contrastText: '#FFFFFF',
      disabled: '#A0C4E0',
    },
    gradients: {
      primary: {
        main: '#1C6BA4',
        state: '#0E4A7A',
      },
    },
    coloredShadows: {
      primary: '#1C6BA4',
    },
    scrollbar: {
      thumb: '#FFC107', // Yellow
      track: 'inherit',
      shadow: 'rgba(255, 193, 7, 0.3)',
    },
  },

  [HMO.MACCABI]: {
    primary: {
      main: '#4A90E2',
      contrastText: '#FFFFFF',
      disabled: '#A8C8F0',
    },
    gradients: {
      primary: {
        main: '#4A90E2',
        state: '#2E6BB8',
      },
    },
    coloredShadows: {
      primary: '#4A90E2',
    },
    scrollbar: {
      thumb: '#FF9800', // Orange
      track: 'inherit',
      shadow: 'rgba(255, 152, 0, 0.3)',
    },
  },

  [HMO.CLALIT]: {
    primary: {
      main: '#00A651',
      contrastText: '#FFFFFF',
      disabled: '#80D3A8',
    },
    gradients: {
      primary: {
        main: '#00A651',
        state: '#007A3D',
      },
    },
    coloredShadows: {
      primary: '#00A651',
    },
    scrollbar: {
      thumb: '#F44335', // Red
      track: 'inherit',
      shadow: 'rgba(244, 67, 53, 0.3)',
    },
  },

  [HMO.MEUCHEDET]: {
    primary: {
      main: '#FF6B35',
      contrastText: '#FFFFFF',
      disabled: '#FFB89A',
    },
    secondary: {
      main: '#004E89',
      contrastText: '#FFFFFF',
    },
    gradients: {
      primary: {
        main: '#FF6B35',
        state: '#E55A2B',
      },
      secondary: {
        main: '#004E89',
        state: '#003A6B',
      },
    },
    coloredShadows: {
      primary: '#FF6B35',
    },
    scrollbar: {
      thumb: '#4CAF50', // Green
      track: 'inherit',
      shadow: 'rgba(76, 175, 80, 0.3)',
    },
  },
  [HMO.CLINICAL_TRIAL]: {
    // Trial-specific colors overrides can be added here
    primary: {
      main: '#1C6BA4',
      contrastText: '#FFFFFF',
      disabled: '#A0C4E0',
    },
  },
};

// Default (no HMO) - empty object
const DEFAULT_COLORS: Partial<Types> = {};

/**
 * Returns HMO-specific colors (React 19 best practice - O(1) lookup from pre-computed constants)
 * @param hmo - HMO identifier or null for default
 * @returns HMO color overrides or empty object for default theme
 */
export function getHmoColors(hmo: HMOType): Partial<Types> {
  if (!hmo) {
    return DEFAULT_COLORS;
  }
  
  return HMO_COLORS[hmo] || DEFAULT_COLORS;
}

